import React, { useState, useEffect } from 'react'
import {
  Box, Typography, Chip, Grid, Avatar, Divider
} from '@mui/material'
import { EmojiEvents, People } from '@mui/icons-material'
import { useParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useEventQuizSocket, playSound } from '../../hooks/useEventQuizSocket'
import EventTimer from '../../components/EventQuiz/EventTimer'
import JoinQRCode from '../../components/EventQuiz/JoinQRCode'
import Leaderboard from '../../components/EventQuiz/Leaderboard'
import BonusWheel from '../../components/EventQuiz/BonusWheel'
import PodiumScreen from '../../components/EventQuiz/PodiumScreen'

const OPTION_COLORS = ['#7b2fff', '#f72585', '#00b4d8', '#06d6a0']
const OPTION_ICONS = ['🔷', '💜', '🩵', '💚']

const MAUVE_BG = 'linear-gradient(135deg, #0d001a 0%, #1a0035 55%, #2a0050 100%)'
const CARD_BG = 'rgba(157, 78, 221, 0.1)'
const BORDER_COLOR = 'rgba(157, 78, 221, 0.35)'
const ACCENT = '#c77dff'

export default function EventQuizDisplay() {
  const { code } = useParams()

  const [phase, setPhase] = useState('waiting')
  const [currentQuestion, setCurrentQuestion] = useState(null)
  const [timerRemaining, setTimerRemaining] = useState(0)
  const [leaderboard, setLeaderboard] = useState([])
  const [correctAnswer, setCorrectAnswer] = useState(null)
  const [bonusWheelSpinning, setBonusWheelSpinning] = useState(false)
  const [bonusResult, setBonusResult] = useState(null)
  const [participantCount, setParticipantCount] = useState(0)
  const [quizTitle, setQuizTitle] = useState('')
  const [questionIndex, setQuestionIndex] = useState(0)
  const [questionCount, setQuestionCount] = useState(0)
  const [firstPlaceAlert, setFirstPlaceAlert] = useState(null)
  const firstPlaceTimeout = React.useRef(null)

  const { connected, state, on } = useEventQuizSocket({
    roomCode: code?.toUpperCase(),
    role: 'display',
    enabled: !!code
  })

  useEffect(() => {
    if (state) {
      setPhase(state.session?.status || 'waiting')
      setLeaderboard(state.session?.leaderboard || [])
      setParticipantCount(state.session?.participantCount || 0)
      setTimerRemaining(state.session?.timerRemaining || 0)
      setQuizTitle(state.quiz?.title || '')
      setQuestionCount(state.quiz?.questionCount || 0)
      if (state.currentQuestion) setCurrentQuestion(state.currentQuestion)
    }
  }, [state])

  useEffect(() => {
    on('event-participant-update', ({ participantCount: c, leaderboard: lb }) => {
      setParticipantCount(c)
      setLeaderboard(lb)
    })
    on('event-countdown', () => setPhase('countdown'))
    on('event-question-start', ({ question, timerRemaining: t, questionIndex: qi }) => {
      setCurrentQuestion(question)
      setTimerRemaining(t)
      setPhase('question')
      setCorrectAnswer(null)
      setBonusResult(null)
      setQuestionIndex(qi ?? 0)
      if (question.isJackpot) playSound('jackpot')
    })
    on('event-timer-tick', ({ timerRemaining: t }) => {
      setTimerRemaining(t)
      if (t <= 5 && t > 0) playSound('tick')
    })
    on('event-timer-end', ({ leaderboard: lb }) => { setLeaderboard(lb); setPhase('review') })
    on('event-review', ({ correctAnswer: ca, leaderboard: lb }) => {
      setCorrectAnswer(ca); setLeaderboard(lb); setPhase('review')
    })
    on('event-leaderboard', ({ leaderboard: lb }) => setLeaderboard(lb))
    on('event-bonus-wheel', ({ result }) => {
      setPhase('bonus-wheel'); setBonusWheelSpinning(true); setBonusResult(null)
      setTimeout(() => { setBonusResult(result); setBonusWheelSpinning(false) }, 4200)
    })
    on('event-bonus-applied', () => setPhase('review'))
    on('event-finished', ({ leaderboard: lb }) => { setLeaderboard(lb); setPhase('finished'); playSound('first-place') })
    on('event-first-place', ({ nickname }) => {
      clearTimeout(firstPlaceTimeout.current)
      setFirstPlaceAlert(nickname)
      playSound('first-place')
      firstPlaceTimeout.current = setTimeout(() => setFirstPlaceAlert(null), 4000)
    })
  }, [on])

  if (phase === 'finished') {
    return <PodiumScreen winners={leaderboard.slice(0, 3)} title="Compétition terminée !" />
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: MAUVE_BG,
      display: 'flex',
      flexDirection: 'column',
      color: 'white',
      overflow: 'hidden',
      fontFamily: '"Inter", "Roboto", sans-serif'
    }}>
      {/* Alerte premier rang */}
      <AnimatePresence>
        {firstPlaceAlert && (
          <motion.div
            initial={{ y: -90, opacity: 0, scale: 0.8 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: -90, opacity: 0, scale: 0.8 }}
            style={{ position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}
          >
            <Box sx={{
              background: 'linear-gradient(90deg, #7b2fff, #f72585)',
              color: 'white',
              px: 4, py: 1.5,
              borderRadius: 6,
              fontWeight: 'bold',
              fontSize: '1.3rem',
              boxShadow: '0 0 50px rgba(123,47,255,0.7), 0 0 20px rgba(247,37,133,0.5)',
              display: 'flex', alignItems: 'center', gap: 1.5,
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <EmojiEvents sx={{ color: '#FFD700' }} /> {firstPlaceAlert} prend la 1ère place !
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Barre supérieure */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        px: 4, py: 1.5,
        background: 'rgba(13,0,26,0.7)',
        borderBottom: `1px solid ${BORDER_COLOR}`,
        backdropFilter: 'blur(12px)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%',
            bgcolor: '#9d4edd',
            boxShadow: '0 0 8px #9d4edd'
          }} />
          <Typography variant="h6" fontWeight="bold" sx={{
            background: 'linear-gradient(90deg, #c77dff, #ffffff)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
          }}>
            {quizTitle || 'Quiz Compétition'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Chip
            icon={<People sx={{ color: ACCENT + ' !important' }} />}
            label={`${participantCount} joueur${participantCount > 1 ? 's' : ''}`}
            sx={{ bgcolor: CARD_BG, color: 'white', border: `1px solid ${BORDER_COLOR}`, fontWeight: 'bold' }}
          />
          {questionCount > 0 && phase === 'question' && (
            <Chip
              label={`Q ${questionIndex + 1} / ${questionCount}`}
              sx={{ background: 'linear-gradient(90deg,#7b2fff,#9d4edd)', color: 'white', fontWeight: 'bold' }}
            />
          )}
          <Chip
            label={code?.toUpperCase()}
            sx={{
              bgcolor: 'rgba(199,125,255,0.15)',
              color: ACCENT,
              fontFamily: 'monospace',
              fontWeight: 'bold',
              letterSpacing: 3,
              fontSize: '1rem',
              border: `1px solid ${BORDER_COLOR}`
            }}
          />
        </Box>
      </Box>

      {/* Zone principale */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Salle d'attente */}
        {phase === 'waiting' && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            <Box sx={{ textAlign: 'center' }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Typography variant="h2" fontWeight="900" gutterBottom sx={{
                  background: 'linear-gradient(135deg, #c77dff 0%, #ffffff 50%, #f72585 100%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  letterSpacing: -1
                }}>
                  Rejoignez la compétition !
                </Typography>
                <Typography variant="h5" sx={{ color: 'rgba(199,125,255,0.8)' }} gutterBottom>
                  Scannez le QR code ou entrez le code
                </Typography>
              </motion.div>

              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, type: 'spring' }}
              >
                <Typography variant="h1" fontWeight="900" sx={{
                  mt: 4,
                  letterSpacing: 12,
                  background: 'linear-gradient(90deg, #9d4edd, #c77dff, #ffffff)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  textShadow: 'none',
                  filter: 'drop-shadow(0 0 30px rgba(157,78,221,0.6))'
                }}>
                  {code?.toUpperCase()}
                </Typography>
              </motion.div>
            </Box>

            <Box sx={{
              p: 3, borderRadius: 4,
              background: 'white',
              boxShadow: '0 0 60px rgba(157,78,221,0.5), 0 0 120px rgba(123,47,255,0.2)',
              border: '3px solid rgba(157,78,221,0.4)'
            }}>
              <JoinQRCode code={code?.toUpperCase()} size={260} />
            </Box>
          </Box>
        )}

        {/* Compte à rebours */}
        {phase === 'countdown' && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <motion.div
              key="countdown"
              initial={{ scale: 2, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 100 }}
            >
              <Typography variant="h1" fontWeight="900" sx={{
                fontSize: '14rem', lineHeight: 1,
                background: 'linear-gradient(135deg, #c77dff, #f72585)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 40px rgba(247,37,133,0.5))'
              }}>
                3
              </Typography>
            </motion.div>
          </Box>
        )}

        {/* Question */}
        {(phase === 'question' || phase === 'review') && currentQuestion && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
            <Grid container spacing={3} sx={{ flex: 1 }}>

              {/* Colonne question */}
              <Grid item xs={12} md={8}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentQuestion.order}
                    initial={{ opacity: 0, y: -30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 30 }}
                    transition={{ duration: 0.45 }}
                    style={{ height: '100%' }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <EventTimer seconds={timerRemaining} large />
                    </Box>

                    {currentQuestion.isJackpot && (
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <motion.div animate={{ scale: [1, 1.05, 1] }} transition={{ repeat: Infinity, duration: 1.2 }}>
                          <Chip
                            label="🎰 QUESTION JACKPOT × 2 POINTS"
                            sx={{
                              background: 'linear-gradient(90deg, #f72585, #ffd700)',
                              color: '#000',
                              fontSize: '1rem',
                              py: 3, px: 2,
                              fontWeight: '900',
                              boxShadow: '0 0 30px rgba(255,215,0,0.5)'
                            }}
                          />
                        </motion.div>
                      </Box>
                    )}

                    <Box sx={{
                      bgcolor: CARD_BG,
                      border: `1px solid ${BORDER_COLOR}`,
                      borderRadius: 4,
                      p: 3,
                      mb: 3,
                      backdropFilter: 'blur(8px)'
                    }}>
                      <Typography variant="h4" fontWeight="bold" textAlign="center" sx={{ lineHeight: 1.4 }}>
                        {currentQuestion.question}
                      </Typography>
                    </Box>

                    {currentQuestion.media?.url && (
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        {currentQuestion.media.type === 'image' && (
                          <img src={currentQuestion.media.url} alt="" style={{ maxHeight: 200, borderRadius: 12, border: `2px solid ${BORDER_COLOR}` }} />
                        )}
                        {currentQuestion.media.type === 'audio' && (
                          <audio controls src={currentQuestion.media.url} style={{ width: '60%' }} autoPlay />
                        )}
                        {currentQuestion.media.type === 'video' && (
                          <video controls src={currentQuestion.media.url} style={{ maxHeight: 180, borderRadius: 12 }} autoPlay />
                        )}
                      </Box>
                    )}

                    {currentQuestion.type === 'anagram' && (
                      <Typography variant="h3" textAlign="center" fontFamily="monospace"
                        letterSpacing={8} sx={{ mb: 3, color: ACCENT }}>
                        {currentQuestion.anagramScramble}
                      </Typography>
                    )}

                    {(currentQuestion.type === 'qcm' || currentQuestion.type === 'true-false') && currentQuestion.options && (
                      <Grid container spacing={2}>
                        {currentQuestion.options.map((opt, i) => {
                          const isCorrect = phase === 'review' && correctAnswer?.id === opt.id
                          return (
                            <Grid item xs={6} key={opt.id}>
                              <motion.div
                                animate={isCorrect ? { scale: [1, 1.04, 1], boxShadow: ['0 0 0px #06d6a0', '0 0 40px #06d6a0', '0 0 20px #06d6a0'] } : {}}
                                transition={{ repeat: isCorrect ? 3 : 0, duration: 0.5 }}
                              >
                                <Box sx={{
                                  background: isCorrect
                                    ? 'linear-gradient(135deg, #06d6a0, #00b4d8)'
                                    : `linear-gradient(135deg, ${OPTION_COLORS[i % 4]}cc, ${OPTION_COLORS[i % 4]}88)`,
                                  borderRadius: 3,
                                  p: '18px 24px',
                                  display: 'flex', alignItems: 'center', gap: 2,
                                  border: isCorrect ? '2px solid #06d6a0' : '1px solid rgba(255,255,255,0.15)',
                                  boxShadow: isCorrect ? '0 0 40px rgba(6,214,160,0.5)' : `0 4px 20px ${OPTION_COLORS[i % 4]}40`,
                                  transition: 'all 0.3s',
                                  backdropFilter: 'blur(4px)'
                                }}>
                                  <Typography variant="h4">{OPTION_ICONS[i % 4]}</Typography>
                                  <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>{opt.text}</Typography>
                                  {isCorrect && <EmojiEvents sx={{ color: '#FFD700', fontSize: 32 }} />}
                                </Box>
                              </motion.div>
                            </Grid>
                          )
                        })}
                      </Grid>
                    )}

                    {phase === 'review' && (currentQuestion.type === 'anagram' || currentQuestion.type === 'quick-answer') && correctAnswer && (
                      <Box sx={{ textAlign: 'center', mt: 3 }}>
                        <Chip
                          label={`✅ Réponse : ${correctAnswer.text}`}
                          sx={{ background: 'linear-gradient(90deg,#06d6a0,#00b4d8)', color: '#000', fontSize: '1.2rem', py: 3, px: 3, fontWeight: 'bold' }}
                        />
                      </Box>
                    )}
                  </motion.div>
                </AnimatePresence>
              </Grid>

              {/* Classement */}
              <Grid item xs={12} md={4}>
                <Box sx={{
                  bgcolor: CARD_BG,
                  border: `1px solid ${BORDER_COLOR}`,
                  borderRadius: 3, p: 2, height: '100%',
                  backdropFilter: 'blur(10px)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <EmojiEvents sx={{ color: '#FFD700' }} />
                    <Typography variant="h6" fontWeight="bold" sx={{ color: ACCENT }}>Top 5</Typography>
                  </Box>
                  <Divider sx={{ borderColor: BORDER_COLOR, mb: 1.5 }} />
                  <Leaderboard entries={leaderboard.slice(0, 5)} />
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Roue bonus */}
        {phase === 'bonus-wheel' && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BonusWheel spinning={bonusWheelSpinning} result={bonusResult} />
          </Box>
        )}
      </Box>

      {/* Pied de page signature */}
      <Box sx={{
        px: 4, py: 1.5,
        background: 'rgba(13,0,26,0.85)',
        borderTop: `1px solid ${BORDER_COLOR}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        backdropFilter: 'blur(12px)'
      }}>
        <Typography variant="caption" sx={{ color: 'rgba(199,125,255,0.55)' }}>
          Cette plateforme a été fièrement construite par{' '}
          <a href="https://www.nouratech.sn" target="_blank" rel="noopener noreferrer"
            style={{ color: '#c77dff', fontWeight: 'bold', textDecoration: 'none' }}>
            Noura Tech
          </a>
        </Typography>

        <Typography variant="caption" sx={{ color: 'rgba(199,125,255,0.35)', fontStyle: 'italic' }}>
          Plateforme de quiz interactif Firdaws
        </Typography>

        <Chip
          label={connected ? '● Live' : '○ Hors ligne'}
          size="small"
          sx={{
            bgcolor: connected ? 'rgba(6,214,160,0.15)' : 'rgba(239,83,80,0.15)',
            color: connected ? '#06d6a0' : '#ef5350',
            border: `1px solid ${connected ? '#06d6a060' : '#ef535060'}`,
            fontWeight: 'bold'
          }}
        />
      </Box>
    </Box>
  )
}
